import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useT, useCanonical } from '@/i18n/dgtgLang';
import Link from '@/lib/siteLink';
import { Helmet } from 'react-helmet-async';
import { ArrowRight } from 'lucide-react';
import DGTGNav from './DGTGNav';
import DGTGFooter from './DGTGFooter';
import AdSlot from '@/components/AdComponents';
import BlogCard from '@/components/BlogCard';
import { HeroProcess, HowWeWorkSection } from './HowWeWork';
import { DGTG_SERVICES, DGTG_BRANDS, DGTG_INDUSTRY_PAGES, DGTG_SOCIALS, DGTG_FOUNDED } from './dgtgData';

const API = process.env.REACT_APP_BACKEND_URL;
const DIVISIONS = [
  { title: 'Performance media', desc: 'Cross-platform campaigns bought and optimised against leads, installs or sales, not impressions.' },
  { title: 'Content studio', desc: 'Short-form video, reels, stories and UGC, made in-house so creative and media move together.' },
  { title: 'Influencer and community', desc: 'Creator programmes across gaming, lifestyle, fintech and entertainment.' },
  { title: 'Regional PR and market entry', desc: 'Localising and launching brands across India, the Middle East and Southeast Asia.' },
];

const REGIONS = ['India', 'Southeast Asia', 'MENA', 'EMEA', 'North America'];

/* Logo strip. It scrolls inside the content width, not the full window, and fades at both edges, so it
   lines up with everything above and below it. Hover pauses it. */
function LogoStrip({ brands }) {
  const row = brands.map((b) => (
    <li key={b.name} className="flex-shrink-0 h-10 flex items-center" title={b.name}>
      {b.logo
        ? <img src={b.logo} alt={b.name} className="h-7 w-auto max-w-[110px] object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition" loading="lazy" />
        : <span className="text-lg font-bold tracking-tight text-gray-400">{b.name}</span>}
    </li>
  ));
  return (
    <div className="overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
      <ul className="flex gap-14 marquee-left hover:[animation-play-state:paused]" style={{ animationDuration: '45s' }} aria-label="Brands we have worked with">
        {row}
        {brands.map((b) => (
          <li key={`${b.name}-2`} className="flex-shrink-0 h-10 flex items-center" aria-hidden="true">
            {b.logo
              ? <img src={b.logo} alt="" className="h-7 w-auto max-w-[110px] object-contain grayscale opacity-60" loading="lazy" />
              : <span className="text-lg font-bold tracking-tight text-gray-400">{b.name}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function DGTGHome() {
  const [blogPosts, setBlogPosts] = useState([]);
  const t = useT();
  const canon = useCanonical();
  const { hash } = useLocation();
  useEffect(() => {
    // #world-data is the old address of this spot (the globe section, replaced 2026-09-28).
    if (hash !== '#how-we-work' && hash !== '#world-data') return undefined;
    let n = 0;
    const timer = setInterval(() => {
      const el = document.getElementById('how-we-work');
      if (el || ++n > 40) { clearInterval(timer); el?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    }, 150);
    return () => clearInterval(timer);
  }, [hash]);

  useEffect(() => {
    fetch(`${API}/api/content/articles?site=dgtg&status=published&lightweight=true&exclude_sponsored=true&limit=3`)
      .then((r) => r.json())
      .then((data) => setBlogPosts(data.articles || []))
      .catch(() => {});
  }, []);

  return (
    <>
      <Helmet>
        <title>{t('DGTG Marketing & Retail Pvt Ltd, Performance Marketing Since 2015')}</title>
        <meta name="description" content={t('DGTG has run performance, content and influencer campaigns since 2015. Our team uses a decade of campaign data, with AI as a tool, to plan, create, run and verify campaigns across India, MENA, SEA, EMEA and North America.')} />
        <meta property="og:title" content={t('DGTG, Community-Powered Growth Agency')} />
        <meta property="og:description" content={t('From clicks to communities, DGTG powers digital growth for the new era.')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canon('/')} />
        <link rel="canonical" href={canon('/')} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "Organization",
          "name": "DGTG Marketing & Retail Private Limited", "url": "https://www.dgtg.in",
          "description": "Community and content-driven growth agency running cross-region social, PR, media buying, and performance programs.",
          "email": "contact@dgtg.in",
          "logo": "https://www.dgtg.in/dgtg-logo.png",
          "foundingDate": String(DGTG_FOUNDED),
          "sameAs": DGTG_SOCIALS.map(x => x.url),
          "address": { "@type": "PostalAddress", "addressLocality": "Whitefield, Bengaluru", "addressRegion": "Karnataka", "addressCountry": "IN" },
          "areaServed": REGIONS.map(r => ({ "@type": "Place", "name": r })),
          "knowsAbout": ["Performance Marketing", "Content Strategy", "Influencer Marketing", "Digital Growth", "Media Buying", "Lead Generation", "App Install Campaigns"]
        })}</script>
      </Helmet>

      {/* Marquee keyframes injected once */}
      <style>{`
        @keyframes marquee-scroll-left { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .marquee-left { animation: marquee-scroll-left var(--speed, 30s) linear infinite; min-width: max-content; }
        @media (prefers-reduced-motion: reduce) { .marquee-left { animation: none; flex-wrap: wrap; } }
      `}</style>

      <DGTGNav />

      {/* Hero: who we are in one line, then the process line full width underneath. */}
      <section className="bg-[#faf9fc] border-b border-gray-100" data-testid="dgtg-hero">
        <div className="max-w-6xl mx-auto px-6 pt-16 md:pt-20 pb-14">
          <div className="grid lg:grid-cols-[1.25fr_1fr] gap-8 lg:gap-16 items-end">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] text-gray-900">
              {t('A decade of marketing know-how, sharpened by AI.')}
            </h1>
            <div>
              <p className="text-lg text-gray-600 leading-relaxed">
                {t('Since {year}, DGTG has run campaigns for brands across India, Southeast Asia, the Middle East, Europe and North America. Our team brings that experience and the data behind it; AI helps them use it faster, so every plan starts from what has worked before.').replace('{year}', DGTG_FOUNDED)}
              </p>
              <Link to="/dgtg/contact" className="mt-6 inline-flex items-center gap-2 bg-[#773DFF] text-white px-7 py-3.5 rounded-full font-bold text-sm hover:bg-[#5B2ACC] transition-colors" data-testid="dgtg-hero-cta">
                {t('Start a project')} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
              </Link>
            </div>
          </div>
          {/* Content in dgtgFlow.js. */}
          <HeroProcess />
        </div>
      </section>

      {/* Brands */}
      <section className="py-14 bg-white border-b border-gray-100" data-testid="dgtg-brands-marquee">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-sm font-semibold text-gray-500 mb-6">{t('Brands we have run campaigns for')}</h2>
          <LogoStrip brands={DGTG_BRANDS} />
        </div>
      </section>

      {/* The six steps in plain words, and why brands pick us. */}
      <HowWeWorkSection />

      {/* Services */}
      <section id="services" className="py-20 bg-white" data-testid="dgtg-services">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-8">{t('What we run')}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 border border-gray-200 rounded-2xl overflow-hidden">
            {DGTG_SERVICES.map(s => (
              <Link key={s.slug} to={`/dgtg/service/${s.slug}`} className="group bg-white p-7 hover:bg-gray-50 transition-colors flex flex-col" data-testid={`home-service-${s.slug}`}>
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#773DFF] transition-colors">{t(s.title)}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed flex-1">{t(s.short)}</p>
                <span className="mt-5 text-sm font-semibold text-[#773DFF] inline-flex items-center gap-1">{t('Details')} <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 rtl:rotate-180" /></span>
              </Link>
            ))}
            {/* Five services leave one cell of the grid empty; it carries the question most visitors have. */}
            {DGTG_SERVICES.length % 3 !== 0 && (
              <Link to="/dgtg/contact" className="group bg-gray-950 text-white p-7 flex flex-col lg:col-span-1 sm:col-span-2">
                <h3 className="text-lg font-bold">{t('Not sure which one fits?')}</h3>
                <p className="mt-2 text-sm text-gray-300 leading-relaxed flex-1">{t('Tell us the number you need to hit and the budget. We will say which mix gets there, or that none will.')}</p>
                <span className="mt-5 text-sm font-semibold text-[#B99AFF] inline-flex items-center gap-1">{t('Talk to us')} <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 rtl:rotate-180" /></span>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section id="industries" className="py-20 bg-gray-50 border-y border-gray-100" data-testid="dgtg-industries">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-[1fr_2fr] gap-10">
          <div>
            <h2 className="text-2xl sm:text-3xl font-black text-gray-900">{t('Industries')}</h2>
            <p className="mt-3 text-gray-600">{t('The verticals where we have run the most campaigns.')}</p>
          </div>
          <ul className="grid sm:grid-cols-2 gap-x-10">
            {DGTG_INDUSTRY_PAGES.map((ind) => (
              <li key={ind.slug} className="border-b border-gray-200">
                <Link to={`/dgtg/industry/${ind.slug}`} className="group flex items-baseline justify-between gap-4 py-4" data-testid={`home-industry-${ind.slug}`}>
                  <span className="font-semibold text-gray-900 group-hover:text-[#773DFF] transition-colors">{t(ind.title)}</span>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-[#773DFF] shrink-0 rtl:rotate-180" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Divisions */}
      <section className="py-20 bg-white" data-testid="dgtg-divisions">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-8">{t('How we are set up')}</h2>
          <dl className="grid md:grid-cols-2 gap-x-12 gap-y-8">
            {DIVISIONS.map(d => (
              <div key={d.title} className="border-t-2 border-gray-900 pt-4">
                <dt className="text-lg font-bold text-gray-900">{t(d.title)}</dt>
                <dd className="mt-2 text-gray-600 leading-relaxed">{t(d.desc)}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="py-8 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <AdSlot site="dgtg" placement="home_mid" maxAds={1} />
        </div>
      </section>

      {/* Blog */}
      {blogPosts.length > 0 && (
        <section className="py-20 bg-gray-50 border-t border-gray-100" data-testid="dgtg-blog-preview">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-baseline justify-between mb-8">
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900">{t('From the blog')}</h2>
              <Link to="/dgtg/blog" className="text-sm font-semibold text-[#773DFF] hover:underline">{t('All articles')}</Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {blogPosts.map((a) => (
                <BlogCard key={a.id} article={{ ...a, site: 'dgtg' }} to={`/dgtg/blog/${a.slug || a.id}`} accent="#773DFF" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Closing */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <p className="text-2xl sm:text-3xl font-black text-white max-w-xl">{t('Have a launch, a target or a number to hit? Tell us.')}</p>
          <Link to="/dgtg/contact" className="inline-flex items-center gap-2 bg-[#773DFF] text-white px-7 py-3.5 rounded-full font-bold text-sm hover:bg-[#5B2ACC] transition-colors w-fit" data-testid="dgtg-cta-bottom">
            {t('Start a project')} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
          </Link>
        </div>
      </section>

      <DGTGFooter />
    </>
  );
}
